import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { MediaTypes } from "../enums";
import {
  CountryWithStateModel,
  MediaModel,
  ProductAttributeMappingsInput,
  RelatedItemsCategoriesModel,
  RelatedItemsPriceCategoriesModel,
} from "../models";
import {
  CustomLabel,
  CustomWidth,
  FlexContainer,
  Input,
  IOption,
  ITableHeaders,
  SaminarOptions,
  SingleSelect,
  StatusCircle,
  SuccessCheck,
  TableContainer, 
  Video,
} from "@mindmemobile/mm-shared-global";
import PriceDescriptionRow from "./PriceDescriptionRow"; 
import TableGrid from "./TableGrid";
import { AccountSettingsStore } from "../store";

type IProps = {
  selectedItem: RelatedItemsCategoriesModel;
  onSelectPrice: (id: string, checked: boolean) => void;
  onSelectVariant: (id: string, checked: boolean) => void;
  toggleCanChooseAvailableVariant: () => void;
  onUpdate: (data: RelatedItemsCategoriesModel) => void;
  multiplePriceSelection: boolean;
  countriesWithState: CountryWithStateModel[];
};

const SelectedItemWrapper = styled.div`
  .variant-block-margin {
    margin: 20px 0px;
    .categories-table.categories-popup {
      table.item-variant-table {
        border-left: none;
        border-right: none;
        border-bottom: none;
        width: 90%;
        thead {
          background-color: #f8f8f8;
        }
        th {
          font-size: 14px;
          font-weight: 400;
          color: #555;
          border-bottom: 0px;
        }
        td {
          border-bottom: 0px;
          font-size: 14px;
          label {
            font-size: 14px;
          }
        }
        tbody {
          max-height: 150px;
        }
      }
    }
  }
  .variant-chkbx {
    display: flex;
    .form-check-input {
      height: 18px;
      width: 18px;
    }
  }
  .variant-chkbx-label {
    max-width: 34ch;
    flex-shrink: 0;
  }
`;
const variantOptions = [
  {
    label: "Customers can choose from all available Variants ",
    value: "true",
  } as IOption,
  {
    label: "Customers can choose a specific Variant only",
    value: "false",
  } as IOption,
];

const SelectedItemContent: React.FC<IProps> = ({
  selectedItem,
  onSelectPrice,
  onSelectVariant,
  toggleCanChooseAvailableVariant,
  onUpdate,
  multiplePriceSelection,
  countriesWithState
}) => {
  function renderMedia(media: MediaModel) {
    const url = media?.fullURL || "";
    switch (media?.type) {
      case MediaTypes.Video:
        return <Video url={url} />;
      default:
        return <>{url && <img src={url} className="selected-product-img" />}</>;
    }
  }

  function productVariantSection() {
    return (
      <>
        <SingleSelect
          selectedKey={
            selectedItem.canChooseAvailableVariants ? "true" : "false"
          }
          nameKeys={["label"]}
          valueKeys={["value"]}
          options={[variantOptions]}
          searchable={false}
          className="dpdn-with-icons item-selected-dropdown"
          onChange={() => toggleCanChooseAvailableVariant()}
        />
        {!selectedItem.canChooseAvailableVariants && renderVariants()}
        {selectedItem.canChooseAvailableVariants && renderAllVariantsContent()}
      </>
    );
  }

  function renderVariants() {
    const columns: ITableHeaders<ProductAttributeMappingsInput>[] = [
      {
        dataKey: "name",
        title: "VARIANT",
        className: "table-col-40",
        cellClassName: "table-col-40",
        onCellRender: (data: ProductAttributeMappingsInput) => {
          const names = data.attributeMappings.map((a) => {
            if (data.isRemoved) return null;
            return a.optionName;
          });
          const disabled =
            selectedItem.isTrackInventory && parseFloat(data.onHand) <= 0;
          return (
            <div>
              <Input
                type="checkbox"
                value={selectedItem.selectedVariantIds.indexOf(data.id) > -1}
                onChange={(e) =>
                  !disabled && onSelectVariant(data.id, e.target.checked)
                }
                label={
                  <FlexContainer spacing={10} padding="0px 15px 0px 5px">
                    <StatusCircle inActive={false} margin="4px 0px 0px 0px" />{" "}
                    <CustomLabel className="variant-chkbx-label">
                      {names.join(" | ")}
                    </CustomLabel>
                  </FlexContainer>
                }
                className="variant-chkbx"
              />
            </div>
          );
        },
      },
      {
        dataKey: "price",
        title: "PRICE",
        className: "table-col-20 text-center",
        cellClassName: "table-col-20 text-center",
        onCellRender: (data: ProductAttributeMappingsInput) => {
          const price = selectedItem?.variants?.priceByVariant
            ? `$${AccountSettingsStore.formatAmount(parseFloat(data.price), true)}`
            : selectedItem?.prices[0]?.displayPrice;

          return <CustomLabel>{price}</CustomLabel>;
        },
      },
      {
        dataKey: "onHand",
        title: "ON HAND",
        className: "table-col-20 text-center",
        cellClassName: "table-col-20 text-center",
        onCellRender: (data: ProductAttributeMappingsInput) => {
          if (!selectedItem?.isTrackInventory) return null;
          return <CustomLabel>{parseFloat(data.onHand)}</CustomLabel>;
        },
      },
    ];

    return (
      <div className="variant-block-margin">
        <TableContainer className="ps-left-margin categories-table categories-popup">
          <TableGrid
            totalItemCount={selectedItem?.variants?.options.length}
            cellSpacing={20}
            data={selectedItem?.variants?.options || []}
            headers={columns}
            height="inherit"
            className="recent-customer-table table item-variant-table variant-table-chckbx"
            countriesWithState={countriesWithState}
          />
        </TableContainer>
      </div>
    );
  }

  // function renderVariants() {
  //     return (
  //         <div className="variant-block-margin">
  //             {/*  */}
  //             {/* <TableContainer className="ps-left-margin categories-table">
  //                 <TableGrid
  //                     totalItemCount={generalSettings.categories.length}
  //                     cellSpacing={20}
  //                     data={generalSettings.categories || []}
  //                     headers={columns()}
  //                     className="recent-customer-table table item-variant-table variant-table-chckbx"
  //                 />
  //             </TableContainer> */}
  //             {/*  */}
  //             {selectedItem?.variants?.options.map((variant, idx) => {
  //                 const names = variant.attributeMappings.map((a) => {
  //                     if (variant.isRemoved) return null;
  //                     return a.optionName;
  //                 });
  //                 const name = names.join(' | ');

  //                 const price = selectedItem.variants.priceByVariant
  //                     ? '$' + variant.price
  //                     : selectedItem.prices[0].displayPrice;

  //                 const onHandContent = selectedItem.isTrackInventory
  //                     ? ` | ${UtilityMethods.numberWithCommas(parseFloat(variant.onHand))}`
  //                     : ``;

  //                 const disabled = selectedItem.isTrackInventory && parseFloat(variant.onHand) <= 0;
  //                 return (
  //                     <FlexContainer
  //                         justifyContent="space-between"
  //                         padding="10px 10px 5px 0px"
  //                         className={disabled ? 'disabled-row' : ''}
  //                         key={idx}
  //                     >
  //                         <div>
  //                             <Input
  //                                 type="checkbox"
  //                                 value={selectedItem.selectedVariantIds.indexOf(variant.id) > -1}
  //                                 onChange={(e) => !disabled && onSelectVariant(variant.id, e.target.checked)}
  //                                 label={
  //                                     <FlexContainer spacing={10} padding="0px 15px 0px 5px">
  //                                         <StatusCircle inActive={false} margin="4px 0px 0px 0px" />{' '}
  //                                         <CustomLabel className="variant-chkbx-label">{name}</CustomLabel>
  //                                     </FlexContainer>
  //                                 }
  //                                 className="variant-chkbx"
  //                             />
  //                         </div>
  //                         <div>
  //                             <FlexContainer alignItems="center" spacing={10} padding="0px 15px 0px 0px">
  //                                 <CustomLabel fontSize={'14px'}>
  //                                     {price}
  //                                     <span className="charcoal-label"> USD{onHandContent}</span>{' '}
  //                                     {onHandContent && <span className="charcoal-label">ON HAND</span>}
  //                                 </CustomLabel>
  //                             </FlexContainer>
  //                         </div>
  //                     </FlexContainer>
  //                 );
  //             })}
  //         </div>
  //     );
  // }

  function renderAllVariantsContent() {
    const variants = selectedItem.variants.options.filter(
      (variant) => !variant.isRemoved
    );
    //generate sum with only available variants
    const totalOnHands = variants.reduce((acc, variant) => {
      return acc + parseFloat(variant.onHand);
    }, 0);

    //give me min and max price from variants
    const prices = selectedItem.variants.priceByVariant
      ? variants.map((variant) => parseFloat(variant.price))
      : [];
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    return (
      <FlexContainer fxLayout="column" spacing={10} padding="40px 0px 40px">
        <CustomLabel>{variants.length} Available Variants</CustomLabel>
        <CustomLabel>{totalOnHands} On Hand</CustomLabel>
        {selectedItem.variants.priceByVariant && (
          <div>
            Prices range from $
            {AccountSettingsStore.formatAmount(minPrice, true)} - $
            {AccountSettingsStore.formatAmount(maxPrice, true)} each
          </div>
        )}
      </FlexContainer>
    );
  }

  function renderInput(price: RelatedItemsPriceCategoriesModel) {
    if (multiplePriceSelection) {
      return (
        <>
          <Input
            value={selectedItem.selectedPriceIds.indexOf(price.id) > -1}
            type="checkbox"
            onChange={(e) => onSelectPrice(price.id, e.target.checked)}
            className=""
            label={
              <>
                <CustomLabel fontWeight="600">{price.name}</CustomLabel>
                {/* <LockIcon src={LockGrey} marginTop="4px" /> */}
                {price.isDefaultPrice && (
                  <SuccessCheck bgSize="18px" height="19px" />
                )}
                <br />
              </>
            }
          />{" "}
          <FlexContainer
            alignItems="center"
            padding="0px 0px 0px 37px"
            className="labels-font-14"
          >
            <PriceDescriptionRow
              billingType={price.billingType}
              terms={price.terms}
              priceId={price.id}
              recurrence={price.recurrence}
              itemType={selectedItem.type}
              hasVariants={selectedItem.hasVariants}
              variants={selectedItem.variants}
              quantity={parseInt(selectedItem.quantity) || 1}
            />
          </FlexContainer>
        </>
      );
    } else {
      return (
        <>
          <Input
            value={selectedItem.selectedPriceIds.indexOf(price.id) > -1}
            type="radio"
            id={`rdb-${price.id}`}
            onChange={(e) => onSelectPrice(price.id, e.target.checked)}
            className=""
            label={
              <>
                <CustomLabel fontWeight="600">{price.name}</CustomLabel>
                {/* <LockIcon src={LockGrey} marginTop="4px" /> */}
                {price.isDefaultPrice && (
                  <SuccessCheck bgSize="18px" height="19px" />
                )}
                <br />
              </>
            }
          />
          <FlexContainer
            alignItems="center"
            padding="0px 0px 0px 37px"
            className="labels-font-14"
          >
            <PriceDescriptionRow
              billingType={price.billingType}
              terms={price.terms}
              priceId={price.id}
              recurrence={price.recurrence}
              itemType={selectedItem.type}
              hasVariants={selectedItem.hasVariants}
              variants={selectedItem.variants}
              quantity={parseInt(selectedItem.quantity) || 1}
            />
          </FlexContainer>
        </>
      );
    }
  }

  return (
    <SelectedItemWrapper>
      <FlexContainer spacing={20} alignItems="center"></FlexContainer>
      <FlexContainer>
        <CustomWidth width="70%">
          {!selectedItem.hasVariants &&
            selectedItem?.prices?.map((price, idx) => {
              return (
                <>
                  <SaminarOptions key={idx} className="saminar-option">
                    {renderInput(price)}
                  </SaminarOptions>
                </>
              );
            })}
          {selectedItem.hasVariants && productVariantSection()}
          <FlexContainer
            alignItems="center"
            spacing={15}
            className="flex-above-input"
          >
            <Input
              type="text"
              value={selectedItem.quantity}
              className="styled-input small-input"
              onChange={(e) =>
                onUpdate(
                  new RelatedItemsCategoriesModel({
                    ...selectedItem,
                    quantity: e.target.value.trim(),
                  })
                )
              }
            />
            <CustomLabel>Quantity</CustomLabel>
          </FlexContainer>
        </CustomWidth>
        <CustomWidth width="30%">
          {/* <CustomLabel fontSize="22px" fontWeight="600" lineHeight="26px" margin="0px 0px 25px 14px">
                        {selectedItem.name}
                    </CustomLabel> */}
          <div className="render-media">
            {renderMedia(selectedItem.medias?.[0])}
          </div>
        </CustomWidth>
      </FlexContainer>
    </SelectedItemWrapper>
  );
};

export default observer(SelectedItemContent);
